import { APITypesV1 } from "@cur8/api-client";
import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { Visit } from "@cur8/rich-entity";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useRoomQueries } from "hooks/queries/useRoomQuery";
import { useTodaysScheduleQuery } from "hooks/queries/useTodaysScheduleQuery";
import { useVisitHistoryQuery } from "hooks/queries/useVisitHistoryQuery";
import { useVisitQueries } from "hooks/queries/useVisitQuery";
import { formatTimeRange } from "lib/formatDateTime";
import { generateVisitTypeLabel } from "lib/generateVisitTypeLabel";
import { groupVisitsByRoom } from "lib/groupVisitsByRoom";
import { getAndParseLocationCookie } from "lib/nekoCookies";
import { useMemo, useState } from "react";
import { getSelectVisitConfirmUrl } from "render/routes/getSelectVisitConfirmUrl";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { RadioGroup } from "render/ui/presentation/RadioGroup";
import { SpinningLoader } from "render/ui/presentation/SpinningLoader";
import styles from "./styles.module.sass";

export function SelectVisitView({
  goTo,
  onBack,
}: {
  goTo: (url: string) => void;
  onBack(): void;
}) {
  const location = getAndParseLocationCookie();
  const siteId = location?.siteId;

  const scheduleQuery = useTodaysScheduleQuery({ siteId });
  const visitsData = useVisitQueries(scheduleQuery.data);
  const scheduledVisits = visitsData
    .filter((visit) => visit.data != null)
    .map((visit) => visit.data)
    .filter((visit) => visit.status === APITypesV1.VisitStatus.Scheduled)
    .sort((a, b) => (a.startTime < b.startTime ? -1 : 1));
  const roomIds = [...new Set(scheduledVisits.map((visit) => visit.roomId))];
  const roomQueries = useRoomQueries(roomIds);
  // Hold the list until rooms resolve, so visits don't flash under
  // "Unassigned" (re-ambiguating same-named members) before regrouping.
  const roomsLoading = roomQueries.some((query) => query.isLoading);
  const rooms = roomQueries
    .filter((room) => room.data != null)
    .map((room) => room.data);
  const roomGroups = groupVisitsByRoom(scheduledVisits, rooms);
  const [selectedVisitId, setSelectedVisitId] = useState<string | undefined>(
    undefined
  );

  const selectedVisit = useMemo(() => {
    return scheduledVisits.find((visit) => visit.visitId === selectedVisitId);
  }, [selectedVisitId, scheduledVisits]);

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <div className={styles.title}>
              <Typography variant="display-s">Manual check-in</Typography>
              <Typography variant="body-m" color="subtle">
                Select the member you want to check in.
              </Typography>
            </div>
            {scheduledVisits.length === 0 ? (
              <Typography variant="body-m">
                No scheduled visits today
              </Typography>
            ) : roomsLoading ? (
              <div className={styles.loader}>
                <SpinningLoader success={false} />
              </div>
            ) : (
              <RadioGroup
                aria-label="select visit"
                onChange={(value: string) => setSelectedVisitId(value)}
              >
                <div className={styles.radioGroup}>
                  {roomGroups.map((group) => (
                    <div key={group.id} className={styles.roomGroup}>
                      <Typography variant="eyebrow-m" color="subtle">
                        {group.label}
                      </Typography>
                      {group.visits.map((visit) => (
                        <ScheduledVisitRadio
                          key={visit.visitId}
                          visit={visit}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </RadioGroup>
            )}
          </section>
        }
        buttons={
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={onBack} fillContainer>
                Back
              </Button>
            }
            right={
              <Button
                state={!selectedVisit ? "disabled" : undefined}
                onPress={() => {
                  if (!selectedVisit) {
                    return;
                  }
                  const { patientId, visitId } = selectedVisit;
                  goTo(getSelectVisitConfirmUrl({ patientId, visitId }));
                }}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                Continue
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}

function ScheduledVisitRadio({ visit }: { visit: Visit }) {
  const patientId = visit.patientId;
  const patientQuery = usePatientQuery({ patientId });
  const visitHistoryQuery = useVisitHistoryQuery({ patientId });
  const visitTypeLabel =
    visitHistoryQuery.data != null
      ? generateVisitTypeLabel({ visit, visitHistory: visitHistoryQuery.data })
      : undefined;

  const fullName = `${patientQuery.data?.name?.firstName} ${patientQuery.data?.name?.lastName}`;
  const countryCode = visit.site.address.countryCode?.toUpperCase();
  const time = formatTimeRange(visit.startTime, visit.endTime, countryCode);

  return (
    <RadioGroup.Radio
      key={visit.visitId}
      value={visit.visitId}
      label={
        <Typography variant="label-m" as="span">
          {fullName}
        </Typography>
      }
      bottomLeft={
        <Typography variant="body-m" as="span" color="subtle">
          {visitTypeLabel}
        </Typography>
      }
      bottomRight={
        <Typography variant="body-m" color="subtle" as="span">
          {time}
        </Typography>
      }
    />
  );
}
